import { useState, useEffect } from 'react';
import api, { handleApiError } from '../services/api';
import { formatDateTime } from '../utils/formatters';
import { Users, Search, Plus, Trash2, ChevronLeft, ChevronRight, UserCheck, UserX } from 'lucide-react';
import toast from 'react-hot-toast';

function UserRow({ user, onActivate, onDeactivate, onDelete }) {
  // Roles are categorical, not severity — keep them in the neutral/brand family.
  const roleBadgeColor = (roleName) => (roleName === 'owner' ? 'badge-primary' : 'badge-gray');

  return (
    <tr>
      <td>
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-50 text-sm font-semibold text-primary-900 ring-1 ring-primary-100 dark:bg-primary-950 dark:text-primary-300 dark:ring-primary-900">
            {user.name?.charAt(0) || 'U'}
          </div>
          <div>
            <p className="font-medium text-gray-900 dark:text-white">{user.name}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">@{user.username}</p>
          </div>
        </div>
      </td>
      <td>
        <div className="flex items-center">
          <span className="text-sm text-gray-700 dark:text-gray-200 truncate max-w-32" title={user.email}>
            {user.email}
          </span>
        </div>
      </td>
      <td>
        <span className={`badge ${roleBadgeColor(user.role?.name)}`}>
          {user.role?.displayName || user.role?.label || user.role?.name || '-'}
        </span>
      </td>
      <td>
        <span className={`badge ${user.is_active ? 'badge-success' : 'badge-danger'}`}>
          {user.is_active ? 'Aktif' : 'Nonaktif'}
        </span>
      </td>
      <td>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {user.last_login ? formatDateTime(user.last_login) : '-'}
        </p>
      </td>
      <td>
        <div className="flex items-center gap-2">
          <button onClick={() => onActivate(user.id)} className="p-1.5 text-success-600 hover:bg-success-50 dark:hover:bg-success-900/20 rounded">
            <UserCheck className="h-4 w-4" />
          </button>
          <button onClick={() => onDeactivate(user.id)} className="p-1.5 text-warning-600 hover:bg-warning-50 dark:hover:bg-warning-900/20 rounded">
            <UserX className="h-4 w-4" />
          </button>
          <button onClick={() => onDelete(user)} className="p-1.5 text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-900/20 rounded">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </td>
    </tr>
  );
}

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [filters, setFilters] = useState({ search: '', role: '', status: '' });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [form, setForm] = useState({ name: '', username: '', email: '', password: '', role_id: '' });
  const [saving, setSaving] = useState(false);

  const roles = [
    { id: '1', name: 'owner', label: 'Owner' },
    { id: '2', name: 'finance', label: 'Finance' },
    { id: '3', name: 'purchasing', label: 'Purchasing' },
    { id: '4', name: 'sales', label: 'Sales' },
    { id: '5', name: 'inventory', label: 'Inventory' },
  ];

  useEffect(() => {
    fetchUsers();
  }, [pagination.page, filters]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = { page: pagination.page, limit: pagination.limit, ...filters };
      const res = await api.get('/users', { params });
      if (res.data.success) {
        setUsers(res.data.data?.users || res.data.data || []);
        setPagination((prev) => ({
          ...prev,
          total: res.data.data?.total || 0,
          totalPages: res.data.data?.totalPages || Math.ceil((res.data.data?.total || 0) / prev.limit),
        }));
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.post('/users', form);
      if (res.data.success) {
        toast.success('User berhasil dibuat');
        setShowCreateModal(false);
        setForm({ name: '', username: '', email: '', password: '', role_id: '' });
        fetchUsers();
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.put(`/users/${selectedUser.id}`, form);
      if (res.data.success) {
        toast.success('User berhasil diperbarui');
        setShowEditModal(false);
        setSelectedUser(null);
        fetchUsers();
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setSaving(false);
    }
  };

  const activateUser = async (id) => {
    try {
      const res = await api.post(`/users/${id}/activate`);
      if (res.data.success) {
        toast.success('User diaktifkan');
        fetchUsers();
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  const deactivateUser = async (id) => {
    try {
      const res = await api.post(`/users/${id}/deactivate`);
      if (res.data.success) {
        toast.success('User dinonaktifkan');
        fetchUsers();
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  const deleteUser = async (user) => {
    if (!confirm(`Yakin hapus user ${user.name}?`)) return;
    try {
      const res = await api.delete(`/users/${user.id}`);
      if (res.data.success) {
        toast.success('User berhasil dihapus');
        fetchUsers();
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="page-title">Manajemen User</h1>
          <p className="page-subtitle">Kelola akun pengguna dan akses sistem</p>
        </div>
        <button onClick={() => setShowCreateModal(true)} className="btn-primary">
          <Plus className="h-4 w-4 mr-2" /> Tambah User
        </button>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                className="input pl-9"
                placeholder="Cari nama, email, username..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && fetchUsers()}
              />
            </div>
            <div>
              <select
                className="input"
                value={filters.role}
                onChange={(e) => setFilters({ ...filters, role: e.target.value })}
              >
                <option value="">Semua Role</option>
                {roles.map((r) => (
                  <option key={r.id} value={r.name}>{r.label}</option>
                ))}
              </select>
            </div>
            <div>
              <select
                className="input"
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <option value="">Semua Status</option>
                <option value="active">Aktif</option>
                <option value="inactive">Nonaktif</option>
              </select>
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={fetchUsers} className="btn-primary btn-sm">Cari</button>
            <button onClick={() => setFilters({ search: '', role: '', status: '' })} className="btn-secondary btn-sm">Reset</button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        {loading ? (
          <div className="card-body">
            <div className="space-y-3">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="flex gap-4 items-center">
                  <div className="skeleton h-4 w-48" />
                  <div className="skeleton h-4 w-36" />
                  <div className="skeleton h-4 w-20" />
                  <div className="skeleton h-4 w-20" />
                  <div className="skeleton h-4 w-24" />
                  <div className="skeleton h-4 w-20" />
                </div>
              ))}
            </div>
          </div>
        ) : users.length === 0 ? (
          <div className="card-body py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mx-auto mb-4">
              <Users className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">Tidak Ada Data</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Belum ada user yang terdaftar.</p>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Terbaru Login</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <UserRow
                      key={u.id}
                      user={u}
                      onActivate={activateUser}
                      onDeactivate={deactivateUser}
                      onDelete={deleteUser}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Menampilkan {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} dari {pagination.total} data
                </p>
                <div className="flex items-center gap-2">
                  <button onClick={() => setPagination((prev) => ({ ...prev, page: Math.max(1, prev.page - 1) }))} disabled={pagination.page <= 1} className="btn btn-sm btn-outline">
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="text-sm text-gray-700 dark:text-gray-300 px-2">Halaman {pagination.page} / {pagination.totalPages}</span>
                  <button onClick={() => setPagination((prev) => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))} disabled={pagination.page >= pagination.totalPages} className="btn btn-sm btn-outline">
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="modal-overlay">
          <div className="bg-white/95 dark:bg-surface-900/95 rounded-3xl border border-white/80 dark:border-white/10 shadow-modal backdrop-blur-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-surface-100 dark:border-surface-800">
              <h3 className="text-base font-semibold text-surface-900 dark:text-white">Tambah User Baru</h3>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-4">
              <div>
                <label className="label">Nama Lengkap</label>
                <input type="text" className="input" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required />
              </div>
              <div>
                <label className="label">Username</label>
                <input type="text" className="input" value={form.username} onChange={(e) => setForm({...form, username: e.target.value})} required />
              </div>
              <div>
                <label className="label">Email</label>
                <input type="email" className="input" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} required />
              </div>
              <div>
                <label className="label">Password</label>
                <input type="password" className="input" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} required />
              </div>
              <div>
                <label className="label">Role</label>
                <select className="input" value={form.role_id} onChange={(e) => setForm({...form, role_id: e.target.value})} required>
                  <option value="">Pilih Role</option>
                  {roles.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                </select>
              </div>
              <div className="mt-6 flex gap-3 border-t border-surface-100 pt-4 dark:border-surface-800">
                <button type="button" onClick={() => setShowCreateModal(false)} className="btn-secondary flex-1">Batal</button>
                <button type="submit" disabled={saving} className="btn-primary flex-1">{saving ? 'Menyimpan...' : 'Simpan'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedUser && (
        <div className="modal-overlay">
          <div className="bg-white/95 dark:bg-surface-900/95 rounded-3xl border border-white/80 dark:border-white/10 shadow-modal backdrop-blur-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-surface-100 dark:border-surface-800">
              <h3 className="text-base font-semibold text-surface-900 dark:text-white">Edit User</h3>
            </div>
            <form onSubmit={handleUpdate} className="p-6 space-y-4">
              <div>
                <label className="label">Nama Lengkap</label>
                <input type="text" className="input" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required />
              </div>
              <div>
                <label className="label">Email</label>
                <input type="email" className="input" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} required />
              </div>
              <div>
                <label className="label">Role</label>
                <select className="input" value={form.role_id} onChange={(e) => setForm({...form, role_id: e.target.value})} required>
                  <option value="">Pilih Role</option>
                  {roles.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                </select>
              </div>
              <div className="mt-6 flex gap-3 border-t border-surface-100 pt-4 dark:border-surface-800">
                <button type="button" onClick={() => {setShowEditModal(false); setSelectedUser(null);}} className="btn-secondary flex-1">Batal</button>
                <button type="submit" disabled={saving} className="btn-primary flex-1">{saving ? 'Menyimpan...' : 'Perbarui'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}